import { nextTick } from 'vue'

const PRODUCT_LIST_POSITION_KEY = '__productListPosition'

type ProductListPosition = {
  path: string
  productId: number
}

const currentPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`

const readStoredPosition = (): ProductListPosition | undefined => {
  try {
    const storedPosition = window.sessionStorage.getItem(PRODUCT_LIST_POSITION_KEY)

    return storedPosition ? JSON.parse(storedPosition) as ProductListPosition : undefined
  } catch {
    return undefined
  }
}

const storePosition = (position: ProductListPosition) => {
  try {
    window.sessionStorage.setItem(PRODUCT_LIST_POSITION_KEY, JSON.stringify(position))
  } catch {
    return
  }
}

const waitForProduct = (productId: number): Promise<HTMLElement | null> => {
  const selector = `[data-product-id="${productId}"]`
  const existingProduct = document.querySelector<HTMLElement>(selector)

  if (existingProduct) {
    return Promise.resolve(existingProduct)
  }

  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      const product = document.querySelector<HTMLElement>(selector)

      if (!product) {
        return
      }

      window.clearTimeout(timeout)
      observer.disconnect()
      resolve(product)
    })
    const timeout = window.setTimeout(() => {
      observer.disconnect()
      resolve(null)
    }, 5000)

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  })
}

const waitForStableLayout = async () => {
  let previousHeight = document.documentElement.scrollHeight
  let stableChecks = 0

  for (let check = 0; check < 10; check++) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 100))

    const currentHeight = document.documentElement.scrollHeight
    stableChecks = currentHeight === previousHeight ? stableChecks + 1 : 0
    previousHeight = currentHeight

    if (check >= 2 && stableChecks >= 2) {
      return
    }
  }
}

export const rememberProductListPosition = (productId: number) => {
  if (!import.meta.client) {
    return
  }

  const position: ProductListPosition = {
    path: currentPath(),
    productId,
  }

  storePosition(position)

  window.history.replaceState(
    {
      ...window.history.state,
      [PRODUCT_LIST_POSITION_KEY]: position,
    },
    '',
  )
}

export const restoreProductListPosition = async (path: string): Promise<boolean> => {
  if (!import.meta.client) {
    return false
  }

  const historyPosition = window.history.state?.[PRODUCT_LIST_POSITION_KEY] as ProductListPosition | undefined
  const position = historyPosition?.path === path ? historyPosition : readStoredPosition()

  if (!position || position.path !== path || !Number.isFinite(position.productId)) {
    return false
  }

  await nextTick()
  const product = await waitForProduct(position.productId)

  if (!product) {
    return false
  }

  await waitForStableLayout()
  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve())
    })
  })

  const productRect = product.getBoundingClientRect()
  const targetTop = window.scrollY
    + productRect.top
    - Math.max((window.innerHeight - productRect.height) / 2, 0)

  window.scrollTo({
    top: Math.max(targetTop, 0),
    behavior: 'instant',
  })

  try {
    window.sessionStorage.removeItem(PRODUCT_LIST_POSITION_KEY)
  } catch {
    return true
  }

  return true
}
